import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getCurrencySymbol } from "../lib/currency";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { ClickableAvatar } from "./ClickableAvatar";
import { DatePicker } from "./DatePicker";
import { toast } from "sonner";
import { Package, Calendar, Percent, CreditCard, SplitSquareVertical, Loader2 } from "lucide-react";
import { MultiSelect } from "./ui/multi-select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolvePhoto = (photo) => {
  if (!photo) return null;
  if (photo.startsWith("http")) return photo;
  return `${BACKEND_URL}${photo.startsWith("/") ? "" : "/"}${photo}`;
};

export function MembershipModal({ open, onOpenChange, member, membership, packages, branchId, onSuccess, isRenewal }) {
  const { organization } = useAuth();
  const currencySymbol = getCurrencySymbol(organization?.currency);

  const isEdit = !!membership && !isRenewal;

  // Only show active packages in the dropdown
  const activePackages = (packages || []).filter(p => p.status === "Active" || !p.status);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    packageId: "", startDate: new Date().toISOString().slice(0, 10), endDate: "",
    paymentMode: "Cash", transactionId: "",
    discountEnabled: false, discountType: "percentage", discountValue: 0,
    partialPayment: false, paidAmount: 0, partialPaymentDate: "",
    notes: "",
  });

  useEffect(() => {
    if (membership) {
      if (isRenewal) {
        setForm({
          packageId: membership.packageId || "",
          startDate: new Date().toISOString().slice(0, 10),
          endDate: "",
          paymentMode: "Cash",
          transactionId: "",
          discountEnabled: false,
          discountType: "percentage",
          discountValue: 0,
          partialPayment: false,
          paidAmount: 0,
          partialPaymentDate: "",
          notes: "",
        });
      } else {
        setForm({
          packageId: membership.packageId || "",
          startDate: membership.startDate?.slice(0, 10) || "",
          endDate: membership.endDate?.slice(0, 10) || "",
          paymentMode: membership.paymentMode || "Cash",
          transactionId: membership.transactionId || "",
          discountEnabled: !!(membership.discountType && membership.discountValue > 0),
          discountType: membership.discountType || "percentage",
          discountValue: membership.discountValue || 0,
          partialPayment: (membership.remainingAmount || 0) > 0,
          paidAmount: membership.paidAmount || 0,
          partialPaymentDate: membership.partialPaymentDate || "",
          notes: membership.notes || "",
        });
      }
    } else {
      setForm({
        packageId: "", startDate: new Date().toISOString().slice(0, 10), endDate: "",
        paymentMode: "Cash", transactionId: "",
        discountEnabled: false, discountType: "percentage", discountValue: 0,
        partialPayment: false, paidAmount: 0, partialPaymentDate: "",
        notes: "",
      });
    }
  }, [membership, open, isRenewal]);

  useEffect(() => {
    if (form.packageId && form.startDate) {
      if (isEdit && membership && form.packageId === membership.packageId && form.startDate === membership.startDate?.slice(0, 10)) {
        return;
      }
      const pkg = packages.find(p => p.id === form.packageId);
      if (pkg) {
        const start = new Date(form.startDate);
        start.setMonth(start.getMonth() + (pkg.durationMonths || 0));
        start.setDate(start.getDate() + (pkg.durationDays || 0));
        setForm(prev => ({ ...prev, endDate: start.toISOString().slice(0, 10) }));
      }
    }
  }, [form.packageId, form.startDate, packages, isEdit, membership]);

  const selectedPkg = packages.find((p) => p.id === form.packageId);
  const baseAmount = selectedPkg?.price || 0;

  let finalAmount = baseAmount;
  if (form.discountEnabled) {
    finalAmount = form.discountType === "percentage"
      ? baseAmount - (baseAmount * form.discountValue / 100)
      : baseAmount - form.discountValue;
  }
  finalAmount = Math.max(0, finalAmount);

  const remaining = form.partialPayment ? Math.max(0, finalAmount - form.paidAmount) : 0;

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    if (!form.packageId) { toast.error("Select a package"); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/memberships/${membership.id}`, {
          packageId: form.packageId, startDate: form.startDate, endDate: form.endDate,
          discountType: form.discountEnabled ? form.discountType : "",
          discountValue: form.discountEnabled ? form.discountValue : 0,
          notes: form.notes,
        });
        toast.success("Membership updated");
      } else {
        await api.post("/memberships", {
          memberId: member.id, packageId: form.packageId,
          startDate: form.startDate, endDate: form.endDate, amount: baseAmount,
          paymentMode: form.paymentMode, transactionId: form.transactionId,
          discountType: form.discountEnabled ? form.discountType : "",
          discountValue: form.discountEnabled ? form.discountValue : 0,
          partialPayment: form.partialPayment,
          paidAmount: form.partialPayment ? form.paidAmount : finalAmount,
          partialPaymentDate: form.partialPayment ? form.partialPaymentDate : "",
          notes: form.notes, branchId: branchId || "",
          renewedFromMembershipId: isRenewal && membership ? membership.id : null,
        });
        toast.success(isRenewal ? "Membership renewed" : "Membership assigned");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const photoSrc = resolvePhoto(member?.photo);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} closeOnEsc={false}>
      <DialogContent className="bg-card border-border max-w-lg p-0 flex gap-2 flex-col max-h-[90vh]">
        <DialogHeader className="px-2 md:px-6 py-2 md:py-4 border-b border-border shrink-0">
          <DialogTitle className="font-heading text-xl uppercase tracking-wide">
            {isEdit ? "Edit" : isRenewal ? "Renew" : "Assign"} Membership
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-2 py-2 md:px-6 md:py-5 relative">
          {loading && (
            <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] flex items-center justify-center rounded-b-xl">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="font-mono">Processing...</span>
              </div>
            </div>
          )}

          <div className={`flex flex-col gap-3 md:gap-4 ${loading ? "pointer-events-none select-none" : ""}`}>

            {/* Member Info */}
            {member && (
              <div className="flex items-center gap- p-2 bg-secondary/30 rounded-xl">
                <ClickableAvatar
                  src={photoSrc}
                  name={member.name}
                  className="h-12 w-12 rounded-xl border border-border shrink-0"
                  fallbackClassName="rounded-xl bg-primary/15 text-primary font-heading text-lg"
                />
                <div>
                  <p className="font-semibold">{member.name}</p>
                  <p className="text-sm text-muted-foreground font-mono">{member.phone}</p>
                </div>
              </div>
            )}

            {/* Package Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Package className="h-3.5 w-3.5" /> Package
              </Label>
              <MultiSelect
                options={activePackages.map(p => ({
                  label: `${p.name} (${currencySymbol}${p.price}) - ${p.durationMonths}m/${p.durationDays}d`,
                  value: p.id
                }))}
                selected={form.packageId ? [form.packageId] : []}
                onChange={(vals) => update("packageId", vals[0] || "")}
                placeholder="Select Package"
                single={true}
                className="h-11"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5" /> Start Date
                </Label>
                <DatePicker className="px-2" value={form.startDate} onChange={(v) => update("startDate", v)} placeholder="Start date" disabled={loading} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5" /> End Date
                </Label>
                <DatePicker className="px-2" value={form.endDate} onChange={(v) => update("endDate", v)} placeholder="End date" disabled={loading} />
              </div>
            </div>

            {/* Package Price */}
            {selectedPkg && (
              <Card className="bg-secondary/20 border-border">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Package Price</span>
                    <span className="text-lg font-bold font-mono">{currencySymbol}{baseAmount.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Discount */}
            <Card className={`border transition-colors ${form.discountEnabled ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'}`}>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${form.discountEnabled ? 'bg-emerald-500/15 text-emerald-500' : 'bg-secondary text-muted-foreground'}`}>
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Discount</p>
                      <p className="text-xs text-muted-foreground">Apply discount to this membership</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.discountEnabled}
                    onCheckedChange={(v) => update("discountEnabled", v)}
                    disabled={loading}
                    data-testid="ms-discount-toggle"
                  />
                </div>
                {form.discountEnabled && (
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <Select value={form.discountType} onValueChange={(v) => update("discountType", v)} disabled={loading}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="fixed">Fixed ({currencySymbol})</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={form.discountValue}
                      onChange={(e) => update("discountValue", parseFloat(e.target.value) || 0)}
                      placeholder="Value"
                      disabled={loading}
                      data-testid="ms-discount-value"
                    />
                  </div>
                )}
                {form.discountEnabled && (
                  <div className="flex justify-between items-center pt-2 border-t border-border">
                    <span className="text-sm text-emerald-500 font-medium">After Discount</span>
                    <span className="text-lg font-bold font-mono text-emerald-500">{currencySymbol}{finalAmount.toLocaleString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Partial Payment */}
            {!isEdit && (
              <Card className={`border transition-colors ${form.partialPayment ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'}`}>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${form.partialPayment ? 'bg-amber-500/15 text-amber-500' : 'bg-secondary text-muted-foreground'}`}>
                        <SplitSquareVertical className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Partial Payment</p>
                        <p className="text-xs text-muted-foreground">Accept partial amount now</p>
                      </div>
                    </div>
                    <Switch
                      checked={form.partialPayment}
                      onCheckedChange={(v) => update("partialPayment", v)}
                      disabled={loading}
                      data-testid="ms-partial-toggle"
                    />
                  </div>
                  {form.partialPayment && (
                    <div className="space-y-3 pt-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Paid Amount ({currencySymbol})</Label>
                        <Input
                          type="number"
                          value={form.paidAmount}
                          onChange={(e) => update("paidAmount", parseFloat(e.target.value) || 0)}
                          disabled={loading}
                          data-testid="ms-paid-amount"
                        />
                      </div>
                      <div className="flex justify-between items-center py-2 px-3 bg-amber-500/10 rounded-lg">
                        <span className="text-sm text-amber-500 font-medium">Remaining</span>
                        <span className="text-lg font-bold font-mono text-amber-500">{currencySymbol}{remaining.toLocaleString()}</span>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Due Date</Label>
                        <DatePicker value={form.partialPaymentDate} onChange={(v) => update("partialPaymentDate", v)} placeholder="Payment due date" disabled={loading} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment Mode + Transaction ID */}
            {!isEdit && (
              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="space-y-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <CreditCard className="h-3.5 w-3.5" /> Payment Mode
                  </Label>
                  <Select value={form.paymentMode} onValueChange={(v) => update("paymentMode", v)} disabled={loading}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Transaction ID</Label>
                  <Input
                    value={form.transactionId}
                    onChange={(e) => update("transactionId", e.target.value)}
                    placeholder="Optional"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={2}
                placeholder="Add any notes..."
                disabled={loading}
              />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-secondary/20 shrink-0">
          <Button
            className="w-full h-11 font-heading uppercase tracking-wider"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="ms-modal-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </span>
            ) : isEdit ? "Update Membership" : isRenewal ? "Renew Membership" : "Assign Membership"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
